import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../api/types";
import { useAuth } from "../store/auth";
import { useLocale } from "../hooks/useLocale";
import styles from "./LoginPage.module.css";

export function SignUpPage() {
  const { t, tOr, locale, setLocale, locales } = useLocale();
  const signUp = useAuth((s) => s.signUp);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [errCode, setErrCode] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setErrCode(null);
    try {
      await signUp(email.trim(), password);
      setSubmitted(true);
    } catch (err) {
      if (err instanceof ApiError) setErrCode(err.code);
      else setErrCode("unknown");
    } finally {
      setBusy(false);
    }
  }

  // Anti-enumeration: backend always returns 200 even for duplicate
  // emails. The "check your inbox" message is identical regardless.
  if (submitted) {
    return (
      <main className={styles.shell}>
        <div className={styles.card}>
          <h1 className={styles.title}>{t("signup.success_title")}</h1>
          <p className={styles.subtitle}>
            {t("signup.success_body", { email: email.trim() })}
          </p>
          <Link to="/login" className={styles.submit}>
            {t("signup.success_back_to_login")}
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.shell}>
      <div className={styles.langToggle}>
        {locales.map((l) => (
          <button
            key={l}
            type="button"
            aria-pressed={l === locale}
            onClick={() => setLocale(l)}
          >
            {l.toUpperCase()}
          </button>
        ))}
      </div>
      <form className={styles.card} onSubmit={onSubmit}>
        <h1 className={styles.title}>{t("signup.title")}</h1>
        <p className={styles.subtitle}>{t("signup.subtitle")}</p>
        <label className={styles.label}>
          <span>{t("signup.email")}</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
        </label>
        <label className={styles.label}>
          <span>{t("signup.password")}</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
            minLength={8}
            maxLength={72}
          />
        </label>
        {errCode && (
          <p className={styles.error}>
            {tOr(`signup.error.${errCode}`, "signup.error.unknown")}
          </p>
        )}
        <button type="submit" className={styles.submit} disabled={busy}>
          {busy ? t("common.loading") : t("signup.submit")}
        </button>
        <div className={styles.links}>
          <Link to="/login">{t("signup.link.have_account")}</Link>
        </div>
      </form>
    </main>
  );
}
